import React, {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
} from "react";
import { styled, useTheme } from "@mui/material/styles";
import { NumericFormat } from "react-number-format";
import { VariableSizeList, ListChildComponentProps } from "react-window";
import { ListSubheader, Typography, useMediaQuery } from "@mui/material";

const LISTBOX_PADDING = 8;

const selectedStyle = (selected: boolean) =>
  selected
    ? {
        backgroundColor: "primary.dark",
        color: "white",
        borderColor: "primary.dark",
        "&:hover": {
          color: "primary.dark",
          backgroundColor: "primary.light",
          borderColor: "primary.light",
        },
      }
    : {
        color: "primary.dark",
        borderColor: "primary.dark",
        "&:hover": {
          backgroundColor: "primary.light",
          borderColor: "primary.light",
        },
      };

const StyledNumericFormat = styled(NumericFormat)(() => ({
  width: "60%",
  backgroundColor: "#FFF",
  borderRadius: "0px",
  '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button':
    {
      WebkitAppearance: "none",
      margin: 0,
    },
  '& input[type="number"]': {
    MozAppearance: "textfield",
  },
}));

// Types for virtualized list items
type GroupHeaderItem = { key: string | number; group: React.ReactNode };
// Option item is a React element's props bag typically coming from Autocomplete options
type OptionItem = { key: string | number } & Record<string, any>;

type RowItem = GroupHeaderItem | OptionItem;

const isGroupHeader = (item: RowItem): item is GroupHeaderItem =>
  Object.hasOwn(item, "group");

const renderRow = (props: ListChildComponentProps<RowItem[]>) => {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  } as React.CSSProperties;

  if (isGroupHeader(dataSet as GroupHeaderItem)) {
    return (
      <ListSubheader key={dataSet?.key} component="div" style={inlineStyle}>
        {dataSet?.group}
      </ListSubheader>
    );
  }

  const { key, ...optionProps } = dataSet as OptionItem;
  return (
    <Typography
      key={key as React.Key}
      component="li"
      {...(optionProps as Record<string, unknown>)}
      noWrap
      style={inlineStyle}
    >
      {dataSet as React.ReactNode}
    </Typography>
  );
};

const OuterElementContext = createContext<React.HTMLAttributes<HTMLDivElement>>(
  {},
);

const OuterElementType = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: unknown) {
  const ref = useRef<VariableSizeList | null>(null);
  useEffect(() => {
    if (
      ref.current != null &&
      typeof (ref.current as any).resetAfterIndex === "function"
    ) {
      (ref.current as any).resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const ListboxComponent = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props as any;
  const itemData: RowItem[] = [];
  React.Children.forEach(children, (item: any) => {
    if (!item) return;
    itemData.push(item);
    if (item.children && Array.isArray(item.children)) {
      itemData.push(...item.children);
    }
  });

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child: RowItem) => {
    return isGroupHeader(child) ? 48 : itemSize;
  };

  const getHeight = () => {
    return itemCount > 8
      ? 8 * itemSize
      : itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList<RowItem[]>
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType as any}
          innerElementType="ul"
          itemSize={(index: number) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

export {
  StyledNumericFormat,
  renderRow,
  OuterElementContext,
  OuterElementType,
  ListboxComponent,
  selectedStyle,
};
